import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  School as SchoolIcon,
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
  PersonAdd as PersonAddIcon,
  Class as ClassIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Mock data - à remplacer par des appels API
const mockStats = {
  totalTeachers: 24,
  activeTeachers: 22,
  totalClasses: 45,
  totalStudents: 687,
  teacherGrowth: 12,
  studentGrowth: 8.5,
};

const mockActivities = [
  { id: 1, type: 'new_teacher', user: 'Marie Dubois', timestamp: new Date().toISOString(), icon: <PersonAddIcon /> },
  { id: 2, type: 'new_class', user: 'Jean Martin', timestamp: new Date(Date.now() - 3600000).toISOString(), icon: <ClassIcon /> },
  { id: 3, type: 'new_teacher', user: 'Sophie Laurent', timestamp: new Date(Date.now() - 7200000).toISOString(), icon: <PersonAddIcon /> },
];

const mockChartData = [
  { month: 'Jan', teachers: 18, students: 520 },
  { month: 'Fév', teachers: 19, students: 540 },
  { month: 'Mar', teachers: 20, students: 580 },
  { month: 'Avr', teachers: 21, students: 620 },
  { month: 'Mai', teachers: 23, students: 660 },
  { month: 'Juin', teachers: 24, students: 687 },
];

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simuler le chargement des données
    setTimeout(() => setLoading(false), 1000);
  }, []);

  const getActivityText = (type: string) => {
    switch (type) {
      case 'new_teacher':
        return 'Nouveau professeur inscrit';
      case 'new_class':
        return 'Nouvelle classe créée';
      default:
        return 'Activité';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Tableau de bord
      </Typography>

      <Grid container spacing={3}>
        {/* Statistiques */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Professeurs
                  </Typography>
                  <Typography variant="h4">
                    {mockStats.totalTeachers}
                  </Typography>
                  <Chip
                    label={`+${mockStats.teacherGrowth}%`}
                    color="success"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <PeopleIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Actifs
                  </Typography>
                  <Typography variant="h4">
                    {mockStats.activeTeachers}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {Math.round((mockStats.activeTeachers / mockStats.totalTeachers) * 100)}% actifs
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                  <TrendingUpIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Classes
                  </Typography>
                  <Typography variant="h4">
                    {mockStats.totalClasses}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Toutes années
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                  <SchoolIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Élèves
                  </Typography>
                  <Typography variant="h4">
                    {mockStats.totalStudents}
                  </Typography>
                  <Chip
                    label={`+${mockStats.studentGrowth}%`}
                    color="success"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
                <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                  <GroupIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Graphique d'évolution */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Évolution sur 6 mois
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={mockChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="teachers"
                  stroke="#8884d8"
                  name="Professeurs"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="students"
                  stroke="#82ca9d"
                  name="Élèves"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Activités récentes */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Activités récentes
            </Typography>
            <List>
              {mockActivities.map((activity) => (
                <ListItem key={activity.id} alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar>{activity.icon}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={activity.user}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="textPrimary">
                          {getActivityText(activity.type)}
                        </Typography>
                        {' — '}
                        {format(new Date(activity.timestamp), 'HH:mm', { locale: fr })}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;